import { useState } from "react";
import { Alert, Button } from "@agentnative/ui";

/**
 * Shows a client secret exactly once. The secret lives only in the caller's component
 * state (never in a query cache, storage or the URL); dismissing drops it and the server
 * cannot show it again because it stores only a hash.
 */
export function OneTimeSecretAlert({
  label,
  clientId,
  secret,
  onDismiss,
}: {
  label: string;
  clientId: string;
  secret: string;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
    } catch {
      setCopied(false); // clipboard blocked: the value is selectable below
    }
  }

  return (
    <Alert variant="warning" title={`${label}: copy the secret now. It cannot be shown again.`}>
      <div role="status" className="mt-2 space-y-2 text-sm">
        <div>
          Client ID: <span className="font-mono">{clientId}</span>
        </div>
        <div>
          <span className="sr-only">Client secret</span>
          <div className="break-all rounded bg-[var(--color-neutral-100)] p-2 font-mono" data-testid="one-time-secret">
            {secret}
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={copy}>
            {copied ? "Copied" : "Copy secret"}
          </Button>
          <Button size="sm" onClick={onDismiss}>
            I have stored it
          </Button>
        </div>
      </div>
    </Alert>
  );
}
